import React, { useEffect, useRef, useState } from 'react';
import anime from 'animejs/lib/anime.es.js';
import { Menu, X } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const Header = () => {
  const headerRef = useRef(null);
  const logoRef = useRef(null);
  const navRef = useRef(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { currentColors } = useTheme();

  useEffect(() => {
    // Header entrance animation with backdrop blur effect
    anime({
      targets: headerRef.current,
      translateY: [-80, 0],
      opacity: [0, 1],
      backdropFilter: ['blur(0px)', 'blur(10px)'],
      duration: 1200,
      delay: 3500,
      easing: 'easeOutElastic(1, .8)'
    });

    // Logo animation with typewriter effect
    const logoText = logoRef.current.textContent;
    logoRef.current.innerHTML = logoText.split('').map((char, index) => 
      `<span style="opacity: 0; display: inline-block; transform: translateY(20px);">${char === ' ' ? '&nbsp;' : char}</span>`
    ).join('');

    anime({
      targets: logoRef.current.querySelectorAll('span'),
      opacity: [0, 1],
      translateY: [20, 0],
      rotateX: [90, 0],
      duration: 600,
      delay: anime.stagger(50, { start: 4000 }),
      easing: 'easeOutQuart'
    });

    // Add logo character hover effect
    setTimeout(() => {
      const logoChars = logoRef.current.querySelectorAll('span');
      logoChars.forEach((char) => {
        char.addEventListener('mouseenter', () => {
          anime({
            targets: char,
            scale: [1, 1.2, 1.1],
            rotateZ: [0, 10, 0],
            duration: 400,
            easing: 'easeOutElastic(1, .6)'
          });
        });
      });
    }, 5000);

    // Navigation items with enhanced stagger and bounce
    anime({
      targets: navRef.current.children,
      opacity: [0, 1],
      translateX: [30, 0],
      scale: [0.8, 1],
      duration: 800,
      delay: anime.stagger(150, { start: 4500 }),
      easing: 'easeOutElastic(1, .6)'
    });

    // Add floating animation to header
    anime({
      targets: headerRef.current,
      translateY: [0, -2, 0],
      duration: 4000,
      loop: true,
      easing: 'easeInOutSine',
      delay: 6000
    });
  }, []);

  const scrollToSection = (sectionId, buttonElement) => {
    // Animate button click
    anime({
      targets: buttonElement,
      scale: [1, 0.95, 1],
      duration: 200,
      easing: 'easeOutQuad'
    });

    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header 
      ref={headerRef}
      className="fixed top-0 left-0 right-0 z-40 backdrop-blur-sm transition-colors duration-500 opacity-0"
      style={{ 
        backgroundColor: `${currentColors.cardBg}90`,
        borderBottom: `1px solid ${currentColors.borderLight}`
      }}
    >
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div 
            ref={logoRef}
            className="font-mono text-xl font-normal uppercase tracking-wide cursor-pointer hover:scale-105 transition-all duration-300"
            style={{ color: currentColors.text }}
            onClick={(e) => scrollToSection('hero', e.currentTarget)}
          >
            Kevin Panchal
          </div>

          {/* Desktop Navigation */}
          <nav ref={navRef} className="hidden md:flex items-center space-x-8">
            <button 
              onClick={(e) => scrollToSection('about', e.currentTarget)}
              className="nav-link font-mono text-xs uppercase tracking-wide transition-all duration-300 hover:scale-110 relative group"
              style={{ color: currentColors.text }}
              onMouseEnter={(e) => {
                anime({
                  targets: e.currentTarget,
                  letterSpacing: ['0px', '1px'],
                  duration: 300,
                  easing: 'easeOutQuart'
                });
              }}
              onMouseLeave={(e) => {
                anime({
                  targets: e.currentTarget,
                  letterSpacing: ['1px', '0px'],
                  duration: 300,
                  easing: 'easeOutQuart'
                });
              }}
            >
              About
              <span 
                className="absolute bottom-0 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-300"
                style={{ backgroundColor: currentColors.accent }}
              />
            </button>
            <button 
              onClick={(e) => scrollToSection('skills', e.currentTarget)}
              className="nav-link font-mono text-xs uppercase tracking-wide transition-all duration-300 hover:scale-110 relative group"
              style={{ color: currentColors.text }}
              onMouseEnter={(e) => {
                anime({
                  targets: e.currentTarget,
                  letterSpacing: ['0px', '1px'],
                  duration: 300,
                  easing: 'easeOutQuart'
                });
              }}
              onMouseLeave={(e) => {
                anime({
                  targets: e.currentTarget,
                  letterSpacing: ['1px', '0px'],
                  duration: 300,
                  easing: 'easeOutQuart'
                });
              }}
            >
              Skills
              <span 
                className="absolute bottom-0 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-300"
                style={{ backgroundColor: currentColors.accent }}
              />
            </button>
            <button 
              onClick={(e) => scrollToSection('projects', e.currentTarget)}
              className="nav-link font-mono text-xs uppercase tracking-wide transition-all duration-300 hover:scale-110 relative group"
              style={{ color: currentColors.text }}
              onMouseEnter={(e) => {
                anime({
                  targets: e.currentTarget,
                  letterSpacing: ['0px', '1px'],
                  duration: 300,
                  easing: 'easeOutQuart'
                });
              }}
              onMouseLeave={(e) => {
                anime({
                  targets: e.currentTarget,
                  letterSpacing: ['1px', '0px'],
                  duration: 300,
                  easing: 'easeOutQuart'
                });
              }}
            >
              Projects
              <span 
                className="absolute bottom-0 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-300"
                style={{ backgroundColor: currentColors.accent }}
              />
            </button>
            <button 
              onClick={(e) => scrollToSection('contact', e.currentTarget)}
              className="nav-link font-mono text-xs uppercase tracking-wide transition-all duration-300 hover:scale-110 relative group"
              style={{ color: currentColors.text }}
              onMouseEnter={(e) => {
                anime({
                  targets: e.currentTarget,
                  letterSpacing: ['0px', '1px'],
                  duration: 300,
                  easing: 'easeOutQuart'
                });
              }}
              onMouseLeave={(e) => {
                anime({
                  targets: e.currentTarget,
                  letterSpacing: ['1px', '0px'],
                  duration: 300,
                  easing: 'easeOutQuart'
                });
              }}
            >
              Contact
              <span 
                className="absolute bottom-0 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-300"
                style={{ backgroundColor: currentColors.accent }}
              />
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden hover:scale-110 transition-all duration-300"
            style={{ color: currentColors.text }}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav 
            className="md:hidden mt-4 pb-4 transition-all duration-300"
            style={{ borderTop: `1px solid ${currentColors.borderLight}` }}
          >
            <div className="flex flex-col space-y-4 pt-4">
              <button 
                onClick={(e) => scrollToSection('about', e.currentTarget)}
                className="text-left font-mono text-xs uppercase tracking-wide hover:scale-105 transition-all duration-300"
                style={{ color: currentColors.text }}
              >
                About
              </button>
              <button 
                onClick={(e) => scrollToSection('skills', e.currentTarget)}
                className="text-left font-mono text-xs uppercase tracking-wide hover:scale-105 transition-all duration-300"
                style={{ color: currentColors.text }}
              >
                Skills
              </button>
              <button 
                onClick={(e) => scrollToSection('projects', e.currentTarget)}
                className="text-left font-mono text-xs uppercase tracking-wide hover:scale-105 transition-all duration-300"
                style={{ color: currentColors.text }}
              >
                Projects
              </button>
              <button 
                onClick={(e) => scrollToSection('contact', e.currentTarget)}
                className="text-left font-mono text-xs uppercase tracking-wide hover:scale-105 transition-all duration-300"
                style={{ color: currentColors.text }}
              >
                Contact
              </button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;